import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Clock, Brain, TrendingUp, TrendingDown, Target, MessageSquare, Zap, AlertCircle, CheckCircle, BarChart3, Activity, Timer, Volume2, Award, ChevronRight, FileText, Play } from 'lucide-react';
import Header from '@/components/ui/Header';
import Button from '@/components/ui/Button';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const MockInterviewDetails = () => {
const [interview, setInterview] = useState(null);
const [isLoading, setIsLoading] = useState(true);

// Mock interview data
const mockInterviewData = {
id: 2,
type: 'mock',
jobTitle: "Full Stack Developer",
company: "Mock Interview - AI",
companyLogo: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=100&h=100&fit=crop&crop=center",
date: "2025-01-22",
time: "16:00",
duration: "45 minutes",
status: "closed", // 'pending', 'ongoing', 'closed'
interviewType: "Technical & Behavioral",

// Performance data (only available when status is 'closed')
performance: {
overallScore: 78,
technicalScore: 82,
behavioralScore: 75,
stressLevel: "Moderate",
overallTime: "42:15",
averageResponseTime: "28s",
fillerWordsScore: 85,
confidenceLevel: 72,
clarityScore: 88,
professionalismScore: 90,

breakdown: {
technical: {
correctAnswers: 7,
totalQuestions: 10,
percentage: 70,
strengths: ["React ecosystem", "Database design", "API development"],
improvements: ["System design", "Scalability concepts", "Security practices"]
},
behavioral: {
score: 75,
strengths: ["Communication", "Problem-solving approach", "Team collaboration"],
improvements: ["Handling conflict", "Leadership examples", "Time management scenarios"]
},
communication: {
pace: "Good",
clarity: "Excellent",
fillerWords: 12,
averageSentenceLength: "Optimal",
vocabularyDiversity: "High"
}
},

questionsAsked: [
{
id: 1,
category: "Technical",
question: "Explain the concept of closures in JavaScript and provide a practical use case.",
userAnswer: "Provided detailed explanation with code example",
feedback: "Excellent answer with practical examples. Well structured.",
score: 9
},
{
id: 2,
category: "Technical",
question: "How would you optimize a React application that's experiencing performance issues?",
userAnswer: "Discussed memoization, code splitting, and lazy loading",
feedback: "Good coverage of optimization techniques. Could mention profiling tools.",
score: 8
},
{
id: 3,
category: "Behavioral",
question: "Tell me about a time when you had to work with a difficult team member.",
userAnswer: "Shared specific example with resolution",
feedback: "Good use of STAR method. Could emphasize the learning outcome more.",
score: 7
},
{
id: 4,
category: "Technical",
question: "What's the difference between SQL and NoSQL databases? When would you use each?",
userAnswer: "Explained differences and use cases with examples",
feedback: "Comprehensive answer. Great real-world examples.",
score: 9
},
{
id: 5,
category: "Behavioral",
question: "Describe a challenging project you worked on and how you overcame obstacles.",
userAnswer: "Detailed project description with challenges and solutions",
feedback: "Well-structured answer. Good emphasis on problem-solving.",
score: 8
}
],

recommendations: [
"Practice system design questions with focus on scalability",
"Prepare more specific examples for conflict resolution scenarios",
"Review security best practices for web applications",
"Work on reducing filler words during responses",
"Continue strong communication and clarity in technical explanations"
]
}
};

useEffect(() => {
loadInterview();
}, []);

const loadInterview = async () => {
setIsLoading(true);
await new Promise(resolve => setTimeout(resolve, 800));
setInterview(mockInterviewData);
setIsLoading(false);
};

const getScoreColor = (score) => {
if (score >= 80) return 'text-green-600';
if (score >= 60) return 'text-yellow-600';
return 'text-red-600';
};

const getScoreBgColor = (score) => {
if (score >= 80) return 'bg-green-100';
if (score >= 60) return 'bg-yellow-100';
return 'bg-red-100';
};

const getStressLevelColor = (level) => {
const levels = {
'Low': 'text-green-600 bg-green-100',
'Moderate': 'text-yellow-600 bg-yellow-100',
'High': 'text-red-600 bg-red-100'
};
return levels[level] || levels['Moderate'];
};

if (isLoading) {
return (
<div className="min-h-screen bg-gray-50 flex items-center justify-center">
<div className="text-center">
<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
<p className="text-gray-600">Loading interview details...</p>
</div>
</div>
);
}

return (
<div className="min-h-screen bg-background">
<Header />


<main className="pt-16">
<div className="max-w-7xl mx-auto px-6 py-8">

{/* Page Header */}
<div className="mb-8">

<div className="flex items-center justify-between mb-4">

<div className="flex items-center gap-4">
<button
onClick={() => window.history.back()}
className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
>
<ArrowLeft className="w-5 h-5" />
</button>
<div className="flex-1">
<div className="flex items-center gap-3 mb-1">
<h1 className="text-2xl font-bold text-gray-900">{interview.jobTitle}</h1>
<span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
AI Mock Interview
</span>
</div>
<div className="flex items-center gap-4 text-sm text-gray-600">
<span className="flex items-center gap-1">
<Calendar className="w-4 h-4" />
{new Date(interview.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
</span>
<span className="flex items-center gap-1">
<Clock className="w-4 h-4" />
{interview.time}
</span>
<span className="flex items-center gap-1">
<Timer className="w-4 h-4" />
{interview.duration}
</span>
</div>
</div>
</div>

<div className="hidden md:flex items-center space-x-3">
<Button
variant="outline"
iconName="RefreshCw"
iconPosition="left"
iconSize={16}
onClick={loadInterview}
disabled={isLoading}
>
Refresh
</Button>

<Button
variant="default"
iconName="Plus"
iconPosition="left"
iconSize={16}
>
Schedule Another Mock Interview
</Button>

<Button
variant="link"
iconName="Download"
iconPosition="left"
iconSize={16}
>
Download Report
</Button>

</div>
</div>



</div>


{interview.status === 'pending' ? (
// Pending State
<div className="space-y-6">
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
<div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
<Brain className="w-10 h-10 text-purple-600" />
</div>
<h2 className="text-2xl font-bold text-gray-900 mb-2">Mock Interview Scheduled</h2>
<p className="text-gray-600 mb-6">
Your AI-powered mock interview is scheduled for {new Date(interview.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} at {interview.time}
</p>

<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-2xl mx-auto">
<div className="flex items-start gap-3">
<AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
<div className="text-left">
<h3 className="font-semibold text-blue-900 mb-1">Reminder Notifications</h3>
<p className="text-sm text-blue-700">
You will receive reminder notifications:
</p>
<ul className="text-sm text-blue-700 mt-2 space-y-1">
<li>• 1 hour before the interview</li>
<li>• 30 minutes before the interview</li>
</ul>
</div>
</div>
</div>

<button className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium">
<Play className="w-5 h-5 inline mr-2" />
Start Interview Now
</button>
</div>

{/* Preparation Tips */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-bold text-gray-900 mb-4">Preparation Tips</h3>
<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
<div className="flex gap-3">
<div className="flex-shrink-0">
<div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
<CheckCircle className="w-5 h-5 text-blue-600" />
</div>
</div>
<div>
<h4 className="font-semibold text-gray-900 mb-1">Test Your Setup</h4>
<p className="text-sm text-gray-600">Ensure your microphone and camera are working properly</p>
</div>
</div>
<div className="flex gap-3">
<div className="flex-shrink-0">
<div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
<FileText className="w-5 h-5 text-green-600" />
</div>
</div>
<div>
<h4 className="font-semibold text-gray-900 mb-1">Review Job Requirements</h4>
<p className="text-sm text-gray-600">Familiarize yourself with the role and required skills</p>
</div>
</div>
<div className="flex gap-3">
<div className="flex-shrink-0">
<div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
<Brain className="w-5 h-5 text-purple-600" />
</div>
</div>
<div>
<h4 className="font-semibold text-gray-900 mb-1">Find a Quiet Space</h4>
<p className="text-sm text-gray-600">Choose a distraction-free environment for the interview</p>
</div>
</div>
<div className="flex gap-3">
<div className="flex-shrink-0">
<div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
<Target className="w-5 h-5 text-yellow-600" />
</div>
</div>
<div>
<h4 className="font-semibold text-gray-900 mb-1">Prepare Examples</h4>
<p className="text-sm text-gray-600">Think of specific examples from your experience</p>
</div>
</div>
</div>
</div>
</div>
) : (
// Completed State with Performance Report
<div className="space-y-6">
{/* Overall Performance Summary */}
<div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg shadow-lg p-8 text-white">
<div className="flex items-center justify-between mb-6">
<div>
<h2 className="text-2xl font-bold mb-2">Interview Performance Report</h2>
<p className="text-purple-100">Completed on {new Date(interview.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
</div>
<div className="text-center">
<div className="text-5xl font-bold mb-1">{interview.performance.overallScore}</div>
<div className="text-sm text-purple-100">Overall Score</div>
</div>
</div>

<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
<div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
<div className="text-2xl font-bold mb-1">{interview.performance.overallTime}</div>
<div className="text-sm text-purple-100">Total Duration</div>
</div>
<div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
<div className="text-2xl font-bold mb-1">{interview.performance.averageResponseTime}</div>
<div className="text-sm text-purple-100">Avg Response</div>
</div>
<div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
<div className="text-2xl font-bold mb-1">{interview.performance.fillerWordsScore}%</div>
<div className="text-sm text-purple-100">Clarity Score</div>
</div>
<div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
<div className="text-2xl font-bold mb-1">{interview.performance.stressLevel}</div>
<div className="text-sm text-purple-100">Stress Level</div>
</div>
</div>
</div>

{/* Detailed Metrics */}
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
{/* Technical Score */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<div className="flex items-center justify-between mb-4">
<div className="flex items-center gap-2">
<div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
<Brain className="w-5 h-5 text-blue-600" />
</div>
<h3 className="font-semibold text-gray-900">Technical</h3>
</div>
<div className={`text-2xl font-bold ${getScoreColor(interview.performance.technicalScore)}`}>
{interview.performance.technicalScore}%
</div>
</div>
<div className="w-full bg-gray-200 rounded-full h-2 mb-4">
<div
className="bg-blue-600 h-2 rounded-full transition-all"
style={{ width: `${interview.performance.technicalScore}%` }}
></div>
</div>
<div className="text-sm text-gray-600">
{interview.performance.breakdown.technical.correctAnswers} out of {interview.performance.breakdown.technical.totalQuestions} questions answered correctly
</div>
</div>

{/* Behavioral Score */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<div className="flex items-center justify-between mb-4">
<div className="flex items-center gap-2">
<div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
<MessageSquare className="w-5 h-5 text-green-600" />
</div>
<h3 className="font-semibold text-gray-900">Behavioral</h3>
</div>
<div className={`text-2xl font-bold ${getScoreColor(interview.performance.behavioralScore)}`}>
{interview.performance.behavioralScore}%
</div>
</div>
<div className="w-full bg-gray-200 rounded-full h-2 mb-4">
<div
className="bg-green-600 h-2 rounded-full transition-all"
style={{ width: `${interview.performance.behavioralScore}%` }}
></div>
</div>
<div className="text-sm text-gray-600">
Strong communication and problem-solving approach
</div>
</div>

{/* Confidence Level */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<div className="flex items-center justify-between mb-4">
<div className="flex items-center gap-2">
<div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
<TrendingUp className="w-5 h-5 text-purple-600" />
</div>
<h3 className="font-semibold text-gray-900">Confidence</h3>
</div>
<div className={`text-2xl font-bold ${getScoreColor(interview.performance.confidenceLevel)}`}>
{interview.performance.confidenceLevel}%
</div>
</div>
<div className="w-full bg-gray-200 rounded-full h-2 mb-4">
<div
className="bg-purple-600 h-2 rounded-full transition-all"
style={{ width: `${interview.performance.confidenceLevel}%` }}
></div>
</div>
<div className="text-sm text-gray-600">
Good confidence in technical responses
</div>
</div>

{/* Clarity Score */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<div className="flex items-center justify-between mb-4">
<div className="flex items-center gap-2">
<div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
<Volume2 className="w-5 h-5 text-yellow-600" />
</div>
<h3 className="font-semibold text-gray-900">Clarity</h3>
</div>
<div className={`text-2xl font-bold ${getScoreColor(interview.performance.clarityScore)}`}>
{interview.performance.clarityScore}%
</div>
</div>
<div className="w-full bg-gray-200 rounded-full h-2 mb-4">
<div
className="bg-yellow-600 h-2 rounded-full transition-all"
style={{ width: `${interview.performance.clarityScore}%` }}
></div>
</div>
<div className="text-sm text-gray-600">
Excellent articulation and speech clarity
</div>
</div>

{/* Professionalism */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<div className="flex items-center justify-between mb-4">
<div className="flex items-center gap-2">
<div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
<Award className="w-5 h-5 text-indigo-600" />
</div>
<h3 className="font-semibold text-gray-900">Professionalism</h3>
</div>
<div className={`text-2xl font-bold ${getScoreColor(interview.performance.professionalismScore)}`}>
{interview.performance.professionalismScore}%
</div>
</div>
<div className="w-full bg-gray-200 rounded-full h-2 mb-4">
<div
className="bg-indigo-600 h-2 rounded-full transition-all"
style={{ width: `${interview.performance.professionalismScore}%` }}
></div>
</div>
<div className="text-sm text-gray-600">
Professional demeanor throughout interview
</div>
</div>

{/* Stress Level */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<div className="flex items-center justify-between mb-4">
<div className="flex items-center gap-2">
<div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
<Activity className="w-5 h-5 text-red-600" />
</div>
<h3 className="font-semibold text-gray-900">Stress Level</h3>
</div>
<div className={`px-3 py-1 rounded-full text-sm font-medium ${getStressLevelColor(interview.performance.stressLevel)}`}>
{interview.performance.stressLevel}
</div>
</div>
<div className="text-sm text-gray-600 mt-4">
Maintained composure during challenging questions
</div>
</div>
</div>

{/* Communication Breakdown */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-bold text-gray-900 mb-4">Communication Analysis</h3>
<div className="grid grid-cols-2 md:grid-cols-5 gap-4">
<div className="text-center p-4 bg-gray-50 rounded-lg">
<div className="text-2xl font-bold text-gray-900 mb-1">{interview.performance.breakdown.communication.pace}</div>
<div className="text-sm text-gray-600">Speaking Pace</div>
</div>
<div className="text-center p-4 bg-gray-50 rounded-lg">
<div className="text-2xl font-bold text-gray-900 mb-1">{interview.performance.breakdown.communication.clarity}</div>
<div className="text-sm text-gray-600">Clarity</div>
</div>
<div className="text-center p-4 bg-gray-50 rounded-lg">
<div className="text-2xl font-bold text-gray-900 mb-1">{interview.performance.breakdown.communication.fillerWords}</div>
<div className="text-sm text-gray-600">Filler Words</div>
</div>
<div className="text-center p-4 bg-gray-50 rounded-lg">
<div className="text-2xl font-bold text-gray-900 mb-1">{interview.performance.breakdown.communication.averageSentenceLength}</div>
<div className="text-sm text-gray-600">Sentence Length</div>
</div>
<div className="text-center p-4 bg-gray-50 rounded-lg">
<div className="text-2xl font-bold text-gray-900 mb-1">{interview.performance.breakdown.communication.vocabularyDiversity}</div>
<div className="text-sm text-gray-600">Vocabulary</div>
</div>
</div>
</div>

{/* Strengths and Areas for Improvement */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<div className="flex items-center gap-2 mb-4">
<TrendingUp className="w-5 h-5 text-green-600" />
<h3 className="text-lg font-bold text-gray-900">Strengths</h3>
</div>
<div className="space-y-3">
{[...interview.performance.breakdown.technical.strengths, ...interview.performance.breakdown.behavioral.strengths].map((strength, index) => (
<div key={index} className="flex items-start gap-2">
<CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
<span className="text-sm text-gray-700">{strength}</span>
</div>
))}
</div>
</div>

<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<div className="flex items-center gap-2 mb-4">
<TrendingDown className="w-5 h-5 text-orange-600" />
<h3 className="text-lg font-bold text-gray-900">Areas for Improvement</h3>
</div>
<div className="space-y-3">
{[...interview.performance.breakdown.technical.improvements, ...interview.performance.breakdown.behavioral.improvements].map((improvement, index) => (
<div key={index} className="flex items-start gap-2">
<AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
<span className="text-sm text-gray-700">{improvement}</span>
</div>
))}
</div>
</div>
</div>

{/* Questions Asked */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
<h3 className="text-lg font-bold text-gray-900 mb-4">Questions & Feedback</h3>
<div className="space-y-4">
{interview.performance.questionsAsked.map((qa, index) => (
<div key={qa.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
<div className="flex items-start justify-between mb-3">
<div className="flex-1">
<div className="flex items-center gap-2 mb-2">
<span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
{qa.category}
</span>
<span className="text-sm text-gray-500">Question {index + 1}</span>
</div>
<p className="font-medium text-gray-900 mb-2">{qa.question}</p>
</div>
<div className={`ml-4 flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${getScoreBgColor(qa.score * 10)}`}>
<span className={`text-lg font-bold ${getScoreColor(qa.score * 10)}`}>{qa.score}</span>
</div>
</div>
<div className="space-y-2">
<div>
<span className="text-sm font-medium text-gray-700">Your Answer: </span>
<span className="text-sm text-gray-600">{qa.userAnswer}</span>
</div>
<div className="bg-blue-50 border border-blue-200 rounded p-3">
<span className="text-sm font-medium text-blue-900">AI Feedback: </span>
<span className="text-sm text-blue-700">{qa.feedback}</span>
</div>
</div>
</div>
))}
</div>
</div>

{/* Recommendations */}
<div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
<div className="flex items-center gap-2 mb-4">
<Target className="w-5 h-5 text-blue-600" />
<h3 className="text-lg font-bold text-gray-900">Personalized Recommendations</h3>
</div>
<div className="space-y-2">
{interview.performance.recommendations.map((rec, index) => (
<div key={index} className="flex items-start gap-3">
<ChevronRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
<span className="text-sm text-gray-700">{rec}</span>
</div>
))}
</div>
</div>


</div>
)}
</div>
</main>
</div>
);
};

export default MockInterviewDetails;